/**
 * Импорт структуры компании из Excel (p2-excel-import, §10.3/§12). Поток:
 *   1) скачать шаблон Центрики (.xlsx) — или взять свой файл;
 *   2) загрузить файл → сервер анализирует его: шаблон сразу идёт в предпросмотр, свой файл
 *      (выгрузка 1С, таблица Excel/Word — structure-import-custom-file) — через шаг «Колонки файла»,
 *      где пользователь подтверждает разметку (`StructureImportMapping`);
 *   3) увидеть секции обновление/создание/предупреждения/ручные решения/деактивация
 *      + отчёт об ошибках по строкам; выбрать решения по manual/warning (по одной строке или
 *      сразу для секции); строке без даты рождения с найденным тёзкой доступно «Обновить
 *      найденного сотрудника» (D2.5, ТЗ §10.3 — решение остаётся ручным);
 *   4) подтвердить импорт (+ отдельный флаг подтверждения деактивации) → сводка.
 *
 * Диф считает сервер (клиенту не доверяем пересчёт). Работает у клиента (свой скоуп)
 * и у команды (скоуп {cid}) через единый CompanyScopeApi.
 */
import { useMemo, useRef, useState, type ChangeEvent } from 'react'
import { CircleHelp, Download, Upload, AlertTriangle } from 'lucide-react'
import {
  ApiError,
  type CompanyScopeApi,
  type ImportAnalysis,
  type ImportHelp,
  type ImportPreview,
  type ImportPreviewRow,
  type ImportResult,
  type ImportRowAction,
  type ImportRowDecision,
  type ImportNewPosition,
  type ImportStaffingPair,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { downloadFromUrl } from '@/lib/download'
import { cn } from '@/lib/utils'
import {
  arrangementNotice,
  isStructureImportFile,
  staffingVacanciesToAdd,
  STRUCTURE_IMPORT_FORMAT_ERROR,
} from '@/lib/structure'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import { StructureImportMapping } from './structure-import-mapping'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/design-system/components/primitives/popover'
import { LabeledCheckbox } from '@/sout/labeled-checkbox'
import {
  DataTable,
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableHeaderCell,
  DataTableCell,
} from '@/design-system/components/data/data-table'

/** Цвет счётчика-бейджа по классификации строки (design D3). */
const ACTION_VARIANT: Record<ImportRowAction, 'green' | 'blue' | 'amber' | 'gray'> = {
  update: 'blue',
  create: 'green',
  warning: 'amber',
  manual: 'gray',
}

/** Решения пользователя по строкам, требующим выбора: номер строки файла → решение. */
type RowDecisions = Record<number, ImportRowDecision>

/**
 * Как выбирается решение в секции: `none` — строки применяются сами (обновление/создание);
 * `warning` — «Создать как нового» или пропуск; `manual` — строка без даты рождения: пропуск,
 * создание или (при найденном тёзке) обновление найденного сотрудника.
 */
type DecisionMode = 'none' | 'warning' | 'manual'

/** Решение строки для кнопки «Обновить найденных, остальных создать». */
function manualBulkDecision(row: ImportPreviewRow): ImportRowDecision {
  return row.suggestedPersonId ? 'update' : 'create'
}

/**
 * Колонка «В структуре» строки без даты рождения: найден ли активный сотрудник с тем же ФИО и
 * сколько их; «Выбран в другой строке» — тёзка один, но его уже сопоставила другая строка файла.
 */
function namesakeText(row: ImportPreviewRow): string {
  const matches = row.nameMatches ?? 0
  if (row.suggestedPersonId) return 'Найден'
  if (matches > 1) return `Найдено: ${matches}`
  if (matches === 1) return 'Выбран в другой строке'
  return 'Не найден'
}

/** Секция предпросмотра по одной классификации (обновление/создание/...). */
function PreviewSection({
  title,
  rows,
  mode,
  decisions,
  onDecisions,
}: {
  title: string
  rows: ImportPreviewRow[]
  mode: DecisionMode
  decisions: RowDecisions
  onDecisions: (next: RowDecisions) => void
}) {
  if (rows.length === 0) return null
  const setAll = (decide: (row: ImportPreviewRow) => ImportRowDecision) => {
    const next: RowDecisions = {}
    for (const r of rows) next[r.rowNumber] = decide(r)
    onDecisions(next)
  }
  return (
    <div className="mb-5">
      <div className="mb-2 flex items-center gap-2">
        <h4 className="text-txt-primary text-14 font-semibold">{title}</h4>
        <Badge variant={ACTION_VARIANT[rows[0].action]}>{rows.length}</Badge>
      </div>
      {mode === 'warning' ? (
        <div className="mb-3 flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => setAll(() => 'create')}>
            Создать все
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setAll(() => 'skip')}>
            Пропустить все
          </Button>
        </div>
      ) : null}
      {mode === 'manual' ? (
        <div className="mb-3 flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => setAll(manualBulkDecision)}>
            Обновить найденных, остальных создать
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setAll(() => 'skip')}>
            Пропустить все
          </Button>
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <DataTable>
          <DataTableHead>
            <DataTableRow>
              <DataTableHeaderCell>Строка</DataTableHeaderCell>
              <DataTableHeaderCell>ФИО</DataTableHeaderCell>
              <DataTableHeaderCell>Подразделение</DataTableHeaderCell>
              <DataTableHeaderCell>Должность</DataTableHeaderCell>
              <DataTableHeaderCell className="whitespace-nowrap">Дата рождения</DataTableHeaderCell>
              {mode === 'manual' ? (
                <DataTableHeaderCell className="whitespace-nowrap">В структуре</DataTableHeaderCell>
              ) : null}
              {mode !== 'none' ? <DataTableHeaderCell>Действие</DataTableHeaderCell> : null}
            </DataTableRow>
          </DataTableHead>
          <DataTableBody>
            {rows.map((r) => (
              <DataTableRow key={r.rowNumber}>
                <DataTableCell className="text-txt-tertiary">{r.rowNumber}</DataTableCell>
                <DataTableCell>{r.fullName}</DataTableCell>
                <DataTableCell>{r.department}</DataTableCell>
                <DataTableCell>{r.position}</DataTableCell>
                <DataTableCell className="whitespace-nowrap">{r.birthDate || '—'}</DataTableCell>
                {mode === 'manual' ? (
                  <DataTableCell className="whitespace-nowrap text-txt-secondary">
                    {namesakeText(r)}
                  </DataTableCell>
                ) : null}
                {mode === 'warning' ? (
                  <DataTableCell>
                    <LabeledCheckbox
                      checked={decisions[r.rowNumber] === 'create'}
                      onCheckedChange={(v) => onDecisions({ [r.rowNumber]: v ? 'create' : 'skip' })}
                      className="text-13"
                    >
                      Создать как нового
                    </LabeledCheckbox>
                  </DataTableCell>
                ) : null}
                {mode === 'manual' ? (
                  <DataTableCell>
                    <Select
                      value={decisions[r.rowNumber] ?? 'skip'}
                      onValueChange={(v) => onDecisions({ [r.rowNumber]: v as ImportRowDecision })}
                    >
                      <SelectTrigger
                        className="w-52 text-left"
                        aria-label={`Действие для строки ${r.rowNumber}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="skip">Пропустить</SelectItem>
                        <SelectItem value="create">Создать как нового</SelectItem>
                        {r.suggestedPersonId ? (
                          <SelectItem value="update">Обновить найденного</SelectItem>
                        ) : null}
                      </SelectContent>
                    </Select>
                  </DataTableCell>
                ) : null}
              </DataTableRow>
            ))}
          </DataTableBody>
        </DataTable>
      </div>
    </div>
  )
}

const NEW_POSITIONS_HINT =
  'Этих должностей ещё нет в справочнике Центрики — строки с ними импортируются как есть: ' +
  'должность в кабинете свободная. Наименования добавятся в справочник, чтобы дальше их можно ' +
  'было выбрать подсказкой.'

/** «строка 3» / «строки 3, 5» — где в файле встретилась должность. */
function rowsLabel(rows: number[]): string {
  return `строк${rows.length > 1 ? 'и' : 'а'} ${rows.join(', ')}`
}

/**
 * Должности файла, которых ещё нет в справочнике (position-free-text). Строки с ними НЕ
 * отклоняются: должность — текстовое поле, а справочник лишь ускоряет заполнение подсказкой.
 * Секция показывает такие наименования списком, чтобы пополнение справочника при подтверждении
 * не было сюрпризом.
 */
function NewPositionsSection({ items }: { items: ImportNewPosition[] }) {
  if (items.length === 0) return null
  return (
    <div className="mb-5 rounded-12 border border-line bg-header p-4">
      <div className="mb-2 flex items-center gap-2">
        <h4 className="text-14 font-semibold text-txt">Новые должности</h4>
        <Badge variant="blue">{items.length}</Badge>
      </div>
      <p className="mb-2 text-13 text-txt-secondary">{NEW_POSITIONS_HINT}</p>
      <ul className="space-y-1 text-13">
        {items.map((u) => (
          <li key={u.value} className="text-txt-secondary">
            <b className="text-txt">«{u.value}»</b> — {rowsLabel(u.rows)}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Штатные единицы пар «подразделение + должность» из файла (structure-import-staffing): в файле, в
 * структуре сейчас и сколько вакантных мест добавит импорт. Число пересчитывается по решениям по
 * строкам и отметке деактивации — так же, как его посчитает подтверждение на сервере.
 */
function StaffingSection({
  pairs,
  vacancies,
}: {
  pairs: ImportStaffingPair[]
  vacancies: Record<string, number>
}) {
  if (pairs.length === 0) return null
  return (
    <div className="mb-5">
      <div className="mb-2 flex items-center gap-2">
        <h4 className="text-14 font-semibold text-txt">Штатные единицы</h4>
        <Badge variant="blue">{pairs.length}</Badge>
      </div>
      <p className="mb-2 text-13 text-txt-secondary">
        Импорт добавит вакантные рабочие места, чтобы штатных единиц должности в подразделении стало
        столько же, сколько в файле. Число пересчитывается по решениям по строкам и деактивации.
        Лишние единицы импорт не удаляет.
      </p>
      <div className="overflow-x-auto">
        <DataTable>
          <DataTableHead>
            <DataTableRow>
              <DataTableHeaderCell>Подразделение</DataTableHeaderCell>
              <DataTableHeaderCell>Должность</DataTableHeaderCell>
              <DataTableHeaderCell className="whitespace-nowrap">В файле</DataTableHeaderCell>
              <DataTableHeaderCell className="whitespace-nowrap">В структуре</DataTableHeaderCell>
              <DataTableHeaderCell className="whitespace-nowrap">Добавить мест</DataTableHeaderCell>
            </DataTableRow>
          </DataTableHead>
          <DataTableBody>
            {pairs.map((pair) => {
              const add = vacancies[pair.key] ?? 0
              const extra = pair.currentUnits - pair.fileUnits
              return (
                <DataTableRow key={pair.key}>
                  <DataTableCell>{pair.department}</DataTableCell>
                  <DataTableCell>{pair.position}</DataTableCell>
                  <DataTableCell className="whitespace-nowrap">
                    {pair.fileUnits}
                    {pair.rounded ? (
                      <span className="block text-12 text-txt-secondary">округлено вверх</span>
                    ) : null}
                  </DataTableCell>
                  <DataTableCell>{pair.currentUnits}</DataTableCell>
                  <DataTableCell className="whitespace-nowrap">
                    <b className={add > 0 ? 'text-txt' : 'font-normal text-txt-secondary'}>{add}</b>
                    {add === 0 && extra > 0 ? (
                      <span className="block text-12 text-txt-secondary">
                        в структуре больше на {extra}
                      </span>
                    ) : null}
                  </DataTableCell>
                </DataTableRow>
              )
            })}
          </DataTableBody>
        </DataTable>
      </div>
    </div>
  )
}

/** Экран предпросмотра + подтверждения (после успешной загрузки). */
function PreviewPanel({
  api,
  preview,
  notice,
  onApplied,
  onReset,
}: {
  api: CompanyScopeApi
  preview: ImportPreview
  /** Сообщение над предпросмотром — сводка распознанного отчёта 1С. */
  notice: string | null
  onApplied: (result: ImportResult) => void
  onReset: () => void
}) {
  const [decisions, setDecisions] = useState<RowDecisions>({})
  const [deactivateConfirmed, setDeactivateConfirmed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updates = preview.rows.filter((r) => r.action === 'update')
  const creates = preview.rows.filter((r) => r.action === 'create')
  const warnings = preview.rows.filter((r) => r.action === 'warning')
  const manuals = preview.rows.filter((r) => r.action === 'manual')

  function mergeDecisions(next: RowDecisions) {
    setDecisions((prev) => ({ ...prev, ...next }))
  }

  async function confirm() {
    setError(null)
    setSubmitting(true)
    try {
      const rowDecisions: Record<string, ImportRowDecision> = {}
      for (const [k, v] of Object.entries(decisions)) rowDecisions[k] = v
      const result = await api.confirmImport({
        importId: preview.importId,
        rowDecisions,
        deactivateConfirmed: deactivateConfirmed && preview.deactivateCandidates.length > 0,
      })
      onApplied(result)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось применить импорт'))
    } finally {
      setSubmitting(false)
    }
  }

  const staffing = preview.staffing ?? []
  const withDeactivation = deactivateConfirmed && preview.deactivateCandidates.length > 0
  const vacancies = useMemo(
    () => staffingVacanciesToAdd(preview, decisions, withDeactivation),
    [preview, decisions, withDeactivation],
  )
  const vacanciesTotal = Object.values(vacancies).reduce((sum, n) => sum + n, 0)

  const s = preview.summary
  return (
    <Card header="Предпросмотр импорта">
      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}

      <SummaryLine
        items={[
          ['Обновление', s.toUpdate],
          ['Создание', s.toCreate],
          ['Предупреждений', s.warnings],
          ['Требуют решения', s.manual],
          ['Ошибок', s.errorsCount],
          ...(staffing.length > 0 ? [['Вакантных мест добавится', vacanciesTotal] as const] : []),
        ]}
      />

      {notice ? (
        <Alert variant="info" className="mb-4">
          {notice}
        </Alert>
      ) : null}

      <NewPositionsSection items={preview.newPositions ?? []} />

      <PreviewSection
        title="Будут обновлены"
        rows={updates}
        mode="none"
        decisions={decisions}
        onDecisions={mergeDecisions}
      />
      <PreviewSection
        title="Будут созданы"
        rows={creates}
        mode="none"
        decisions={decisions}
        onDecisions={mergeDecisions}
      />
      <PreviewSection
        title="Предупреждения (совпало ФИО, отличается дата рождения)"
        rows={warnings}
        mode="warning"
        decisions={decisions}
        onDecisions={mergeDecisions}
      />
      <PreviewSection
        title="Требуют ручного решения (нет даты рождения)"
        rows={manuals}
        mode="manual"
        decisions={decisions}
        onDecisions={mergeDecisions}
      />

      {preview.errors.length > 0 ? (
        <div className="mb-5">
          <div className="mb-2 flex items-center gap-2">
            <AlertTriangle className="size-4 text-amber" aria-hidden />
            <h4 className="text-txt-primary text-14 font-semibold">Ошибки по строкам</h4>
            <Badge variant="red">{preview.errors.length}</Badge>
          </div>
          <p className="mb-2 text-13 text-txt-secondary">
            Эти строки не будут импортированы. Исправьте их в файле и при желании загрузите повторно —
            остальные валидные строки импортируются.
          </p>
          <ul className="space-y-1 text-13">
            {preview.errors.map((e) => (
              <li key={e.rowNumber} className="text-txt-secondary">
                Строка <b className="text-txt-primary">{e.rowNumber}</b>: {e.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {preview.deactivateCandidates.length > 0 ? (
        <div className="mb-5 rounded-12 border border-line bg-header p-4">
          <div className="mb-2 flex items-center gap-2">
            <h4 className="text-txt-primary text-14 font-semibold">Будут деактивированы</h4>
            <Badge variant="amber">{preview.deactivateCandidates.length}</Badge>
          </div>
          <p className="mb-2 text-13 text-txt-secondary">
            Эти активные сотрудники отсутствуют в загруженном файле. Деактивация мягкая: сотрудники не
            удаляются, история заказов и обучения сохраняется. Без отметки ниже деактивация не выполняется.
          </p>
          <ul className="mb-3 space-y-1 text-13 text-txt-secondary">
            {preview.deactivateCandidates.map((c) => (
              <li key={c.personId}>{c.fullName}</li>
            ))}
          </ul>
          <LabeledCheckbox
            checked={deactivateConfirmed}
            onCheckedChange={(v) => setDeactivateConfirmed(Boolean(v))}
            className="text-13"
          >
            Деактивировать отсутствующих сотрудников
          </LabeledCheckbox>
        </div>
      ) : null}

      <StaffingSection pairs={staffing} vacancies={vacancies} />

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => void confirm()} disabled={submitting}>
          {submitting ? 'Применение…' : 'Применить импорт'}
        </Button>
        <Button variant="secondary" onClick={onReset} disabled={submitting}>
          Отмена
        </Button>
      </div>
    </Card>
  )
}

/**
 * Строка счётчиков «Обновление: 1 · Создание: 2 …»: каждый счётчик переносится целиком, подпись не
 * отрывается от числа на узкой карточке (в модалке).
 */
function SummaryLine({ items }: { items: ReadonlyArray<readonly [string, number]> }) {
  return (
    <p className="mb-4 flex flex-wrap gap-x-2 text-14 text-txt-secondary">
      {items.map(([label, value], i) => (
        <span key={label} className="whitespace-nowrap">
          {i > 0 ? '· ' : null}
          {label}: <b>{value}</b>
        </span>
      ))}
    </p>
  )
}

/** Итоговая сводка после применения импорта. */
function ResultPanel({ result, onDone }: { result: ImportResult; onDone: () => void }) {
  return (
    <Card header="Импорт применён">
      <Alert variant="info" className="mb-4">
        {result.vacanciesCreated
          ? 'Импорт завершён, вакантные рабочие места добавлены по штатным единицам файла. Признак «сменное рабочее место» при необходимости задайте в кабинете вручную.'
          : 'Импорт завершён. Признаки «сменное рабочее место» и «без сотрудника» при необходимости задайте в кабинете вручную.'}
      </Alert>
      <SummaryLine
        items={[
          ['Создано', result.created],
          ['Обновлено', result.updated],
          ['Деактивировано', result.deactivated],
          ['Пропущено', result.skipped],
          ...(result.vacanciesCreated
            ? [['Добавлено вакантных мест', result.vacanciesCreated] as const]
            : []),
        ]}
      />
      <Button onClick={onDone}>Готово</Button>
    </Card>
  )
}

/**
 * Справка «?» к загрузке файла (замечание F2-05): подсказка по клику (`Popover` ДС) у кнопок
 * загрузки — не сдвигает карточку, закрывается крестиком, Escape и кликом вне подсказки. Содержимое
 * запрашивается лениво при первом открытии и кешируется на время жизни компонента; источник данных —
 * бэк (`GET .../structure/import/help`), тот же `imports.SheetColumns` / `ImportantNote`, что
 * генерирует шаблон и валидирует парсер — справка не может разойтись с ними.
 */
/**
 * Подсказка к колонке без повтора «обязательно»: обязательность в подсказке показана звёздочкой, а
 * текст `hint` общий со справкой в шаблоне, где звёздочки нет.
 */
function importHelpHint(column: ImportHelp['columns'][number]): string {
  if (!column.required) return column.hint
  return column.hint.replace(/,?\s*обязательно$/, '') || column.hint
}

function ImportHelpDisclosure({ api }: { api: CompanyScopeApi }) {
  const [open, setOpen] = useState(false)
  const [help, setHelp] = useState<ImportHelp | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onOpenChange(next: boolean) {
    setOpen(next)
    if (next && !help && !loading) {
      setLoading(true)
      setError(null)
      try {
        setHelp(await api.importHelp())
      } catch (err) {
        setError(getErrorMessage(err, 'Не удалось загрузить справку'))
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <Popover open={open} onOpenChange={(next) => void onOpenChange(next)}>
      <PopoverTrigger
        aria-label="Что должно быть в файле — справка по колонкам шаблона"
        className={cn(
          'ds-focus-ring inline-flex size-9 shrink-0 items-center justify-center rounded-full border transition-colors',
          // Открытая справка — кнопка «горит» (как активный пункт меню); закрытая — подсвечивается
          // при наведении тем же акцентом, чтобы было видно, что это кнопка.
          open
            ? 'border-accent bg-accent-bg text-accent-txt'
            : 'border-line-strong text-txt-secondary hover:border-accent hover:bg-accent-bg hover:text-accent-txt',
        )}
      >
        <CircleHelp className="size-4 stroke-current" aria-hidden />
      </PopoverTrigger>
      <PopoverContent title="Что должно быть в файле">
        {loading ? <p className="px-4 py-3 text-13 text-txt-secondary">Загрузка справки…</p> : null}
        {error ? (
          <div className="p-3">
            <Alert variant="danger">{error}</Alert>
          </div>
        ) : null}
        {help ? (
          <>
            <p className="px-4 pt-3 text-13 text-txt-secondary">
              Колонки шаблона Центрики, <span className="text-red">*</span> — обязательные. В
              своём файле колонки могут называться иначе — кабинет предложит, какие загрузить.
            </p>
            <dl className="mx-4 my-2 divide-y divide-line">
              {help.columns.map((c) => (
                <div key={c.header} className="flex gap-3 py-2">
                  <dt className="w-36 shrink-0 text-13 font-medium text-txt">
                    {c.header}
                    {c.required ? (
                      <>
                        <span className="text-red" aria-hidden="true">
                          {' '}
                          *
                        </span>
                        <span className="sr-only"> (обязательная)</span>
                      </>
                    ) : null}
                  </dt>
                  <dd className="text-13 text-txt-secondary">{importHelpHint(c)}</dd>
                </div>
              ))}
            </dl>
            <p className="mx-3 mb-3 rounded-8 bg-header px-3 py-2.5 text-12 text-txt-secondary">
              {help.note}
            </p>
          </>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}

export function StructureImport({
  api,
  onImported,
}: {
  api: CompanyScopeApi
  /**
   * Импорт применён (после успешного `confirmImport`) — F2-17: модалка «Добавить в структуру»
   * использует это, чтобы закрыться и перезагрузить таблицу ОТ. Необязательный — самостоятельное
   * использование (structure-manager.tsx) продолжает показывать итоговую сводку без изменений.
   */
  onImported?: (result: ImportResult) => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  // Свой файл ждёт подтверждения колонок: сам файл уходит в предпросмотр повторно (D2.7).
  const [mapping, setMapping] = useState<{ file: File; analysis: ImportAnalysis } | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  // Сообщение над предпросмотром — сводка распознанного отчёта 1С «Штатная расстановка».
  const [arrangementSummary, setArrangementSummary] = useState<string | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function handleApplied(applied: ImportResult) {
    setResult(applied)
    onImported?.(applied)
  }

  function downloadTemplate() {
    downloadFromUrl(api.importTemplateUrl())
  }

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    // Сбрасываем input, чтобы повторный выбор того же файла снова триггерил change.
    e.target.value = ''
    if (!file) return
    setError(null)
    setResult(null)

    if (!isStructureImportFile(file.name)) {
      setError(STRUCTURE_IMPORT_FORMAT_ERROR)
      return
    }
    setUploading(true)
    try {
      const analysis = await api.analyzeImport(file)
      // Шаблон Центрики и отчёт 1С «Штатная расстановка» — формат известен, сразу предпросмотр.
      if (analysis.format === 'template' || analysis.format === 'zup_arrangement') {
        setPreview(await api.uploadImportPreview(file))
        setArrangementSummary(analysis.arrangement ? arrangementNotice(analysis.arrangement) : null)
      } else {
        setMapping({ file, analysis })
      }
    } catch (err) {
      if (err instanceof ApiError && err.details && err.details.length > 0) {
        setError(err.details[0].message)
      } else {
        setError(getErrorMessage(err, 'Не удалось разобрать файл'))
      }
    } finally {
      setUploading(false)
    }
  }

  function reset() {
    setMapping(null)
    setPreview(null)
    setArrangementSummary(null)
    setResult(null)
    setError(null)
  }

  if (result) {
    return <ResultPanel result={result} onDone={reset} />
  }
  if (preview) {
    return (
      <PreviewPanel
        api={api}
        preview={preview}
        notice={arrangementSummary}
        onApplied={handleApplied}
        onReset={reset}
      />
    )
  }
  if (mapping) {
    const { file } = mapping
    return (
      <StructureImportMapping
        api={api}
        file={file}
        analysis={mapping.analysis}
        onPreview={setPreview}
        onBack={reset}
      />
    )
  }

  return (
    <Card header="Импорт структуры">
      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}
      <p className="mb-4 text-14 text-txt-secondary">
        Загрузите структуру компании из файла: заполненный шаблон Центрики или свой файл — отчёт 1С
        («Штатная расстановка», «Штатные сотрудники»), штатное расписание или таблицу с
        подразделениями, должностями и сотрудниками. Подходят Excel (.xlsx, .xls), Word (.docx) и
        PDF с текстом (не скан). Для своего файла кабинет предложит, какие колонки загрузить и что
        считать строками-заголовками. Перед применением вы увидите предпросмотр синхронизации по
        ключу ФИО + дата рождения и вакантные места по штатным единицам.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          onClick={downloadTemplate}
          leftIcon={<Download className="size-4 stroke-current" aria-hidden />}
        >
          Скачать шаблон структуры
        </Button>
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          leftIcon={<Upload className="size-4 stroke-current" aria-hidden />}
        >
          {uploading ? 'Загрузка…' : 'Загрузить файл'}
        </Button>
        <ImportHelpDisclosure api={api} />
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,.docx,.pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
          className="hidden"
          onChange={(e) => void onFile(e)}
        />
      </div>
    </Card>
  )
}
